import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ActionIcon, Button, Group, TextInput, Tooltip } from "@mantine/core";
import { useDocumentTitle } from "@mantine/hooks";
import {
  IconRefresh,
  IconSearch,
  IconUserPlus,
  IconX,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { WithSidebar } from "@layouts/WithSidebar";

import { PAGE_TITLE, QP } from "@constants/index";

import { patientQKeys } from "@hooks/queries/patient/queryKeys";
import { useRefreshAnimation } from "@hooks/useRefreshAnimation";

import { PatientsTable } from "./components/PatientsTable";
import { StyledPatients } from "./Patients.styled";


export const PatientList = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("patients")} • ${PAGE_TITLE}`);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [searchFilter, setSearchFilter] = useQueryParam(
    QP.SEARCH,
    withDefault(StringParam, "")
  );
  const [_, setTotalNumOfPatients] = useState(0);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const { buttonRef } = useRefreshAnimation({ isRefreshing });

  return (
    <WithSidebar>
      <StyledPatients>
        <div className="header">
          <Group gap={"xs"}>
            <h1>{t("all-patients")}</h1>
            {/* <Badge color="accent.4">{totalNumOfPatients}</Badge> */}
            <Tooltip
              label={t("refresh-data")}
              position="right-end"
              fz={12}
              events={{ focus: true, hover: true, touch: true }}>
              <ActionIcon
                ref={buttonRef}
                variant={"default"}
                size={"md"}
                onClick={() => {
                  queryClient.invalidateQueries({
                    queryKey: patientQKeys.all(),
                  });
                }}>
                <IconRefresh
                  className={"refresh-icon"}
                  color="var(--mantine-color-black)"
                  size={16}
                />
              </ActionIcon>
            </Tooltip>
          </Group>

          <TextInput
            placeholder={t("search-for-patient")}
            w={{ base: 250, md: 350, lg: 400 }}
            value={searchFilter}
            onChange={e => setSearchFilter(e.currentTarget.value, "replace")} // Using replace so that only the last value of searchFilter is tracked by the browser for backward navigation
            leftSection={<IconSearch size={16} />}
            rightSection={
              searchFilter && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => setSearchFilter(null)}>
                  <IconX size={16} />
                </ActionIcon>
              )
            }
          />

          <Button
            onClick={() => {
              navigate("create");
            }}
            leftSection={<IconUserPlus size={16} />}>
            {t("create-patient")}
          </Button>
        </div>

        <PatientsTable
          searchFilter={searchFilter}
          setIsRefreshing={setIsRefreshing}
          setTotalNumOfPatients={setTotalNumOfPatients}
        />
      </StyledPatients>
    </WithSidebar>
  );
};
